// File: src/components/ui/dialog.jsx
import React from "react";

const DialogContext = React.createContext({
  open: false,
  setOpen: () => {},
});

const cn = (...classes) => classes.filter(Boolean).join(" ");

const Dialog = ({ open = false, onOpenChange, children }) => {
  const contextValue = React.useMemo(
    () => ({
      open,
      setOpen: typeof onOpenChange === "function" ? onOpenChange : () => {},
    }),
    [open, onOpenChange]
  );

  return <DialogContext.Provider value={contextValue}>{children}</DialogContext.Provider>;
};

const DialogTrigger = ({ asChild = false, children }) => {
  const { setOpen } = React.useContext(DialogContext);

  if (!children) return null;

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, {
      onClick: (e) => {
        children.props?.onClick?.(e);
        setOpen(true);
      },
    });
  }

  return (
    <button type="button" onClick={() => setOpen(true)}>
      {children}
    </button>
  );
};

const DialogContent = ({ children, className = "" }) => {
  const { open, setOpen } = React.useContext(DialogContext);

  if (!open) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      setOpen(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={handleOverlayClick}
    >
      <div
        className={cn(
          `
            bg-[#111] border border-neutral-700 rounded-2xl shadow-2xl
            w-[min(1120px,100vw-2rem)]
            md:w-[min(1320px,100vw-4rem)]
            max-h-[min(94vh,calc(100dvh-2rem))]
            p-0
            flex flex-col overflow-hidden
          `,
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
};

const DialogHeader = ({ children, className = "" }) => (
  <div className={cn("mb-4 border-b border-neutral-700 pb-2", className)}>{children}</div>
);

const DialogTitle = ({ children, className = "" }) => (
  <h2 className={cn("text-lg font-semibold text-white", className)}>{children}</h2>
);

const DialogDescription = ({ children, className = "" }) => (
  <p className={cn("text-sm text-slate-300", className)}>{children}</p>
);

const DialogFooter = ({ children, className = "" }) => (
  <div className={cn("mt-4 flex justify-end gap-2", className)}>{children}</div>
);

export {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
};